import React from "react";

/**
 * Lightweight skeleton primitives used in place of the old "blur the content + spinner" loading
 * overlays. They render a solid surface (no backdrop blur) with shimmering placeholder shapes, so a
 * loading screen reads as "content is arriving here" rather than dimming whatever was on screen.
 */

/** A single shimmering bar. Width/height via Tailwind classes on `className`. */
export const SkeletonBar: React.FC<{ className?: string }> = ({ className = "h-4 w-full" }) => (
	<div className={`animate-pulse rounded bg-slate-200 ${className}`} />
);

/** A list of skeleton rows (icon block + two text lines) — mimics a content/table list. */
export const SkeletonList: React.FC<{ rows?: number; className?: string }> = ({ rows = 8, className }) => (
	<div className={`space-y-2.5 ${className ?? ""}`}>
		{Array.from({ length: rows }).map((_, i) => (
			<div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
				<div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-slate-200" />
				<div className="min-w-0 flex-1 space-y-2">
					<div className="h-3 animate-pulse rounded bg-slate-200" style={{ width: `${42 + ((i * 11) % 38)}%` }} />
					<div className="h-2.5 animate-pulse rounded bg-slate-200" style={{ width: `${22 + ((i * 7) % 28)}%` }} />
				</div>
				<div className="h-5 w-16 shrink-0 animate-pulse rounded-full bg-slate-200" />
			</div>
		))}
	</div>
);

/** A grid of skeleton cards — mimics card/overview layouts. */
export const SkeletonCards: React.FC<{ count?: number; minWidth?: number }> = ({ count = 8, minWidth = 190 }) => (
	<div className="grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` }}>
		{Array.from({ length: count }).map((_, i) => (
			<div key={i} className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
				<div className="h-3.5 w-3/4 animate-pulse rounded bg-slate-200" />
				<div className="h-2.5 w-full animate-pulse rounded bg-slate-200" />
				<div className="h-2.5 w-2/3 animate-pulse rounded bg-slate-200" />
			</div>
		))}
	</div>
);
